/**
 * Content Editor Page - Clean Design
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAppStore } from '../stores';
import { MarkdownEditor } from '../components/MarkdownEditor';

const API_URL = '';

export function ContentEditor() {
    const { collectionId, contentId } = useParams();
    const navigate = useNavigate();
    const { collections } = useAppStore();

    const collection = collections.find((c) => c.id === collectionId);
    const isNew = !contentId;

    const [formData, setFormData] = useState<Record<string, unknown>>({});
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    // Load existing content
    useEffect(() => {
        if (contentId && collection) {
            const loadContent = async () => {
                try {
                    const slug = collection.slug || collection.tableName;
                    const response = await fetch(`${API_URL}/api/v1/content/${slug}`);
                    const result = await response.json();
                    if (result.success && result.data) {
                        const item = result.data.find((c: any) => c.id === contentId);
                        if (item) {
                            setFormData(item);
                            if (item.content) {
                                // Content is already a string in Markdown mode
                            }
                        }
                    }
                } catch (error) {
                    console.error('Failed to load content:', error);
                }
            };
            loadContent();
        }
    }, [contentId, collection, collectionId]);

    // Field change handler
    const handleFieldChange = (fieldName: string, value: unknown) => {
        setFormData((prev) => ({ ...prev, [fieldName]: value }));
    };

    const handleSave = async (publish = false) => {
        setSaving(true);
        setMessage('');

        try {
            const slug = collection?.slug || collection?.tableName || collectionId;
            const url = isNew
                ? `${API_URL}/api/v1/content/${slug}`
                : `${API_URL}/api/v1/content/${slug}/${contentId}`;

            const method = isNew ? 'POST' : 'PATCH';

            const body = {
                ...formData,
                status: publish ? 'published' : 'draft',
            };

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            const result = await response.json();

            if (result.success) {
                setMessage('✅ Saved successfully!');
                setTimeout(() => {
                    navigate(`/collections/${collectionId}`);
                }, 800);
            } else {
                setMessage(`❌ Error: ${result.error?.message || 'Save failed'}`);
            }
        } catch (error) {
            setMessage(`❌ Error: ${String(error)}`);
        } finally {
            setSaving(false);
        }
    };

    if (!collection) {
        return (
            <div className="animate-fade-in py-12">
                <div className="card p-12 text-center max-w-md mx-auto">
                    <p style={{ color: 'var(--text-secondary)' }} className="mb-6">Collection not found.</p>
                    <button onClick={() => navigate('/')} className="btn btn-primary w-full">Back home</button>
                </div>
            </div>
        );
    }

    return (
        <div className="animate-fade-in flex flex-col h-full -m-6 h-[calc(100vh-3.5rem)]">
            {/* Action Bar */}
            <div
                className="h-14 flex items-center justify-between px-6 z-10"
                style={{ backgroundColor: 'var(--bg-primary)', borderBottom: '1px solid var(--border-color)' }}
            >
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/collections/${collectionId}`)}
                        className="btn btn-ghost px-2"
                        title="Go back"
                    >
                        ←
                    </button>
                    <div className="hidden md:block">
                        <span style={{ color: 'var(--text-muted)' }} className="text-sm">
                            {collection.name} /
                        </span>
                        <span className="ml-1 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {isNew ? 'New Entry' : 'Edit Entry'}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    {message && (
                        <span className="text-sm font-medium" style={{ color: message.includes('✅') ? 'var(--success)' : 'var(--danger)' }}>
                            {message}
                        </span>
                    )}
                    <button
                        className="btn btn-secondary text-sm"
                        onClick={() => handleSave(false)}
                        disabled={saving}
                    >
                        Save Draft
                    </button>
                    <button
                        className="btn btn-primary text-sm px-6"
                        onClick={() => handleSave(true)}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Publish'}
                    </button>
                </div>
            </div>

            {/* Editor Workspace */}
            <div className="flex-1 flex overflow-hidden">
                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto p-6 md:p-12 lg:px-24">
                    <div className="max-w-4xl mx-auto space-y-8">
                        {/* Title Display */}
                        <div className="space-y-2">
                            <input
                                type="text"
                                placeholder={`Enter ${collection.name.toLowerCase()} title...`}
                                className="w-full text-4xl font-extrabold bg-transparent border-none outline-none focus:ring-0 p-0"
                                style={{ color: 'var(--text-primary)' }}
                                value={(formData.title as string) || (formData.name as string) || ''}
                                onChange={(e) => handleFieldChange('title', e.target.value)}
                            />
                            <div className="h-0.5 w-12 bg-accent rounded-full opacity-50"></div>
                        </div>

                        {/* Editor Container */}
                        <div className="min-h-[500px]">
                            <MarkdownEditor
                                value={(formData.content as string) || ''}
                                onChange={(val: string) => handleFieldChange('content', val)}
                                placeholder="Start writing your legal document in Markdown..."
                            />
                        </div>
                    </div>
                </div>

                {/* Sidebar - Property Panel */}
                <aside
                    className="w-80 shrink-0 hidden lg:flex flex-col overflow-y-auto p-6"
                    style={{ backgroundColor: 'var(--bg-primary)', borderLeft: '1px solid var(--border-color)' }}
                >
                    <h3 className="text-xs font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-muted)' }}>
                        Settings
                    </h3>

                    <div className="space-y-6">
                        {/* Slug Field */}
                        <div>
                            <label className="label">Slug / URL</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--text-muted)' }}>/</span>
                                <input
                                    type="text"
                                    className="input pl-6 text-xs"
                                    placeholder="auto-generated"
                                    value={(formData.slug as string) || (formData.title as string || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}
                                    onChange={(e) => handleFieldChange('slug', e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Custom Fields Section */}
                        {collection.fields?.filter(f => f.name !== 'title' && f.name !== 'content' && f.type !== 'richtext' && f.name !== 'slug').length > 0 && (
                            <>
                                <hr style={{ borderColor: 'var(--border-color)' }} />
                                <h4 className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
                                    Metadata
                                </h4>
                                {collection.fields
                                    .filter(f => f.name !== 'title' && f.name !== 'content' && f.type !== 'richtext' && f.name !== 'slug')
                                    .map(field => (
                                        <div key={field.name}>
                                            <label className="label">{field.label || field.name}</label>
                                            {field.type === 'boolean' ? (
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!formData[field.name]}
                                                        onChange={e => handleFieldChange(field.name, e.target.checked)}
                                                    />
                                                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>Enabled</span>
                                                </div>
                                            ) : (
                                                <input
                                                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                                    className="input text-sm"
                                                    placeholder={field.label || field.name}
                                                    value={(formData[field.name] as string) || ''}
                                                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                                                />
                                            )}
                                        </div>
                                    ))}
                            </>
                        )}

                        <hr style={{ borderColor: 'var(--border-color)' }} />

                        {/* Summary / Excerpt? */}
                        <div>
                            <label className="label">Status</label>
                            <div className="flex gap-2">
                                <span className="w-2.5 h-2.5 rounded-full mt-1.5" style={{
                                    backgroundColor: (formData.status === 'published') ? 'var(--success)' : 'var(--text-muted)'
                                }}></span>
                                <div>
                                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                        {((formData.status as string) || 'Draft').toUpperCase()}
                                    </div>
                                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                        {isNew ? 'Not yet saved' : `Created ${new Date(String(formData.created_at)).toLocaleDateString()}`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}
